import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from './store';
import { 
	ExamStateType, 
	ExamType, 
	QuestionType, 
	SchoolTestTableType, 
	SchoolTestType, 
	StartExamQuestionsType, 
	StartExamRequestType, 
	StartExamType, 
	StudentExamsType
} from '../common/school-tests';

import {
	createSchoolTestService,
	deleteQuestionService,
	editQuestionService,
	deleteSchoolTestService,
	editSchoolTestService,
	getMySchoolTestService,
	getSchoolTestByIdService,
	createSchoolTestQuestionService,
	createSchoolExamService,
	searchUsersService,
	getMyExamsService,
	getSchoolExamByIdService,
	getMySchoolTestListService,
	schoolExamAnswerService,
	schoolExamStartedService,
	getMyStudentsExamsService
} from '../services/schoolTestsAxiosServices';
import { SearchUser } from '../common/user-model';


interface SchoolTestSliceType {
	loadingCreateSchoolTest: boolean;
	errorCreateSchoolTest: string | undefined;
	mySchoolTests: SchoolTestTableType[];
	counts: number | null;
	pages: number;
	loadingMySchoolTests: boolean;
	errorMySchoolTests: string | undefined;
	loadingEditSchoolTest: boolean;
	errorEditSchoolTest: string | undefined;
	loadingSchoolTestDetails: boolean;
	errorSchoolTestDetails: string | undefined;
	schoolTestDetails: SchoolTestType;
	loadingDeleteSchoolTest: boolean;
	errorDeleteSchoolTest: string | undefined;
	loadingDeleteSchoolTestQuestion: boolean;
	errorDeleteSchoolTestQuestion: string | undefined;
	loadingEditQuestion: boolean;
	errorEditQuestion: string | undefined;
	loadingDeleteQuestion: boolean;
	errorDeleteQuestion: string | undefined;
	loadingCreateSchoolTestQuestion: boolean;
	errorCreateSchoolTestQuestion: string | undefined;
	loadingCreateExam: boolean;
	errorCreateExam: string | undefined;
	searchUsersLoading: boolean;
	searchUsersError: string | undefined;
	searchUsersList: SearchUser[];
	loadingGetExams: boolean;
	errorGetExams: string | undefined;
	examsList: ExamStateType[];
	examDetails: ExamStateType;
	loadingExamDetails: boolean;
	errorExamDetails: string | undefined;
	schoolTestsList: SchoolTestTableType[];
	loadingSchoolTestsList: boolean;
	errorSchoolTestsList: string | undefined;
	loadingExamAnswer: boolean;
	errorExamAnswer: string | undefined;
	loadingExamStart: boolean;
	errorExamStart: string | undefined;
	schoolExamTest: StartExamType;
	studentsExamsList?: StudentExamsType[];
	loadingStudentsExamsList?: boolean;
	errorStudentsExamsList?: string | undefined;

}

export const initialState: SchoolTestSliceType = {
	loadingCreateSchoolTest: false,
	errorCreateSchoolTest: undefined,
	mySchoolTests: [],
	counts: null,
	pages: 0,
	loadingMySchoolTests: false,
	errorMySchoolTests: undefined,
	loadingEditSchoolTest: false,
	errorEditSchoolTest: undefined,
	loadingSchoolTestDetails: false,
	errorSchoolTestDetails: undefined,
	schoolTestDetails: {
		name: "",
		description: "",
		excellentScoreValue: 0,
		veryGoodScoreValue: 0,
		goodScoreValue: 0,
		satisfyScoreValue: 0,
		failScoreValue: 0,
		questions: []
	},
	loadingDeleteSchoolTest: false,
	errorDeleteSchoolTest: undefined,
	loadingDeleteSchoolTestQuestion: false,
	errorDeleteSchoolTestQuestion: undefined,
	loadingEditQuestion: false,
	errorEditQuestion: undefined,
	loadingDeleteQuestion: false,
	errorDeleteQuestion: undefined,
	loadingCreateSchoolTestQuestion: false,
	errorCreateSchoolTestQuestion: undefined,
	loadingCreateExam: false,
	errorCreateExam: undefined,
	searchUsersLoading: false,
	searchUsersError: undefined,
	searchUsersList: [],
	loadingGetExams: false,
	errorGetExams: undefined,
	examsList: [],
	examDetails: {
		id: "",
		schoolTest: {
			id: "",
			name: "",
			description: "",
			excellentScoreValue: 0,
			veryGoodScoreValue: 0,
			goodScoreValue: 0,
			satisfyScoreValue: 0,
			failScoreValue: 0,
			//questions: []
		},
		student: {
			studentId: "",
			firstName: "",
			lastName: "",
			avatarUrl: ""
		},
		teacher: {
			teacherId: "",
			firstName: "",
			lastName: "",
			avatarUrl: ""
		},
		startExamAt: "",
		endExamAt: "",
		isStarted: false,
		createAt: "",
		score: 0,
		answers: [],
		schoolTestQuestionsCount: 0
	},
	loadingExamDetails: false,
	errorExamDetails: undefined,
	schoolTestsList: [],
	loadingSchoolTestsList: false,
	errorSchoolTestsList: undefined,
	loadingExamAnswer: false,
	errorExamAnswer: undefined,
	loadingExamStart: false,
	errorExamStart: undefined,
	schoolExamTest: {
		exam: {
			examId: "",
			startAt: "",
			endAt: "",
			isStarted: false
		},
		questions: [],

	},
	studentsExamsList: [],
	loadingStudentsExamsList: false,
	errorStudentsExamsList: undefined,
}


export const createSchoolTest = createAsyncThunk('schoolTests/createSchoolTest', async (schoolTest: SchoolTestType) => {
	try {
		await createSchoolTestService(schoolTest)
	} catch (error) {
		console.error(error)
		throw error;
	}
})

export const createSchoolExam = createAsyncThunk('schoolTests/createSchoolExam', async (schoolExam: ExamType) => {
	try {
		await createSchoolExamService(schoolExam)
	} catch (error) {
		console.error(error)
		throw error;
	}
})

export const searchForUsers = createAsyncThunk('schoolTests/searchForUsers', async (search: string) => {
	let response;
	try {
		response = await searchUsersService(search)
	} catch (error) {
		console.error(error)
		throw error
	}
	return response?.data
})

export const createSchoolTestQuestion = createAsyncThunk('schoolTests/createSchoolTestQuestion', async (schoolTestQuestion: QuestionType) => {
	try {
		await createSchoolTestQuestionService(schoolTestQuestion)
	} catch (error) {
		console.error(error)
		throw error;
	}
})

export const editSchoolTest = createAsyncThunk('schoolTests/editSchoolTest', async (schoolTest: SchoolTestType) => {
	try {
		await editSchoolTestService(schoolTest)
	} catch (error) {
		console.error(error)
		throw error
	}
})

export const editSchoolTestQuestion = createAsyncThunk('schoolTests/editSchoolTestQuestion', async (question: QuestionType) => {
	try {
		await editQuestionService(question)
	} catch (error) {
		console.error(error)
		throw error
	}
})

export const deleteSchoolTest = createAsyncThunk('schoolTests/deleteSchoolTest', async (schoolTestId: string) => {
	try {
		await deleteSchoolTestService(schoolTestId)
	} catch (error) {
		console.error(error)
		throw error
	}
})

export const deleteSchoolTestQuestion = createAsyncThunk('schoolTests/deleteSchoolTestQuestion', async (schoolTestQuestionId: string) => {
	try {
		await deleteQuestionService(schoolTestQuestionId)
	} catch (error) {
		console.error(error)
		throw error
	}
})

interface GetSchoolTestsType {
	userId: string;
	sortBy: string;
	sortOrder: string;
	page: number;
	testId?: string;
}

export const getMySchoolTests = createAsyncThunk('schoolTests/getMySchoolTests', async ({
	userId,
	page,
	sortBy,
	sortOrder,
}: GetSchoolTestsType) => {
	let response;
	try {
		response = await getMySchoolTestService(userId, page, sortBy, sortOrder)
	} catch (error) {
		console.error(error)
		throw error;
	}
	return response?.data
})

export const getMySchoolTestsList = createAsyncThunk('schoolTests/getMySchoolTestsList', async (
	userId: string
) => {
	let response;
	try {
		response = await getMySchoolTestListService(userId)
	} catch (error) {
		console.error(error)
		throw error;
	}
	return response?.data
})

export const getMySchoolExams = createAsyncThunk('schoolTests/getMySchoolExams', async ({
	userId,
	page,
	sortBy,
	sortOrder,
	testId
}: GetSchoolTestsType) => {
	let response;
	try {
		response = await getMyExamsService(userId, page, sortBy, sortOrder, testId)
	} catch (error) {
		console.error(error)
		throw error;
	}
	return response?.data
})

export const getMyStudentsSchoolExams = createAsyncThunk('schoolTests/getMyStudentsSchoolExams', async ({
	userId,
	page,
	sortBy,
	sortOrder,
}: GetSchoolTestsType) => {
	let response;
	try {
		response = await getMyStudentsExamsService(userId, page, sortBy, sortOrder)
	} catch (error) {
		console.error(error)
		throw error;
	}
	return response?.data
})

export const getSchoolTestDetails = createAsyncThunk('schoolTests/getSchoolTestDetails', async (schoolTestId: string) => {
	let response;
	try {
		response = await getSchoolTestByIdService(schoolTestId)
	} catch (error) {
		console.error(error)
		throw error;
	}
	return response?.data
})

export const getSchoolExamDetails = createAsyncThunk('schoolTests/getSchoolExamDetails', async (schoolExamId: string) => {
	let response;
	try {
		response = await getSchoolExamByIdService(schoolExamId)
	} catch (error) {
		console.error(error)
		throw error;
	}
	return response?.data
})

export const startSchoolExam = createAsyncThunk('schoolTests/startSchoolExam', async ({
	examId,
	questionId,
	studentAnswer
}: StartExamRequestType) => {
	try {
		await schoolExamAnswerService({
			examId,
			questionId,
			studentAnswer
		})
	} catch (error) {
		console.error(error)
		throw error;
	}
})

export const answerSchoolExamQuestions = createAsyncThunk('schoolTests/answerSchoolExamQuestions', async (examId: string) => {
	let response;
	try {
		response = await schoolExamStartedService(examId)
	} catch (error) {
		console.error(error)
		throw error;
	}
	return response?.data
})



export const schoolTestsSlice = createSlice({
	name: 'schoolTests',
	initialState,
	reducers: {
		cleanCreteError: (state: SchoolTestSliceType) => {
			state.errorCreateSchoolTest = undefined
		},
		cleanSearchResult: (state: SchoolTestSliceType) => {
			state.searchUsersList = []
		},
		cleanExamsList: (state: SchoolTestSliceType) => {
			state.examsList = []
		}
	},
	extraReducers: (builder) => {
		builder
			.addCase(getMySchoolTestsList.pending, (state: SchoolTestSliceType) => {
				state.errorSchoolTestsList = undefined
				state.loadingSchoolTestsList = true
			})
			.addCase(getMySchoolTestsList.fulfilled, (state: SchoolTestSliceType, action) => {
				state.loadingSchoolTestsList = false
				console.log(action.payload)
				const allSchoolTests: SchoolTestTableType[] = []
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				action.payload.forEach((schoolTest: any) => {
					const schoolTetsObject = {
						name: schoolTest.name,
						id: schoolTest.id,
						description: schoolTest.description,
						createdBy: schoolTest.created_by,
						createdAt: schoolTest.date_created,
						updatedAt: schoolTest.date_modified,
					}
					allSchoolTests.push(schoolTetsObject)
				})
				state.schoolTestsList = allSchoolTests
			})
			.addCase(getMySchoolTestsList.rejected, (state: SchoolTestSliceType, action) => {
				state.loadingSchoolTestsList = false
				state.errorSchoolTestsList = action.error.message
			}),
			builder
				.addCase(getMySchoolTests.pending, (state: SchoolTestSliceType) => {
					state.errorMySchoolTests = undefined
					state.loadingMySchoolTests = true
				})
				.addCase(getMySchoolTests.fulfilled, (state: SchoolTestSliceType, action) => {
					state.loadingMySchoolTests = false
					state.counts = action.payload.count
					const pages = Math.ceil(action.payload.count / 20)
					state.pages = action.payload.count / 20 < 0 ? 2 : pages
					const allSchoolTests: SchoolTestTableType[] = []
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					action.payload.results.forEach((schoolTest: any) => {
						const schoolTetsObject = {
							name: schoolTest.name,
							id: schoolTest.id,
							description: schoolTest.description,
							createdBy: schoolTest.created_by,
							createdAt: schoolTest.date_created,
							updatedAt: schoolTest.date_modified,
						}
						allSchoolTests.push(schoolTetsObject)
					})
					state.mySchoolTests = allSchoolTests
				})
				.addCase(getMySchoolTests.rejected, (state: SchoolTestSliceType, action) => {
					state.loadingMySchoolTests = false
					state.errorMySchoolTests = action.error.message
				}),
			builder
				.addCase(getMySchoolExams.pending, (state: SchoolTestSliceType) => {
					state.errorGetExams = undefined
					state.loadingGetExams = true
				})
				.addCase(getMySchoolExams.fulfilled, (state: SchoolTestSliceType, action) => {
					state.loadingGetExams = false
					state.counts = action.payload.count
					const pages = Math.ceil(action.payload.count / 20)
					state.pages = action.payload.count / 20 < 0 ? 2 : pages
					const allSchoolExams: ExamStateType[] = []
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					action.payload.results.forEach((exam: any) => {
						const schoolExamObject = {
							id: exam.id,
							schoolTest: {
								id: exam.school_test.id,
								name: exam.school_test.name,
								description: exam.school_test.description,
								excellentScoreValue: exam.school_test.excellent_score_value,
								veryGoodScoreValue: exam.school_test.very_good_score_value,
								goodScoreValue: exam.school_test.good_score_value,
								satisfyScoreValue: exam.school_test.satisfy_score_value,
								failScoreValue: exam.school_test.fail_score_value,
							},
							teacher: {
								teacherId: exam.teacher.id,
								firstName: exam.teacher.first_name,
								lastName: exam.teacher.last_name,
								avatarUrl: exam.teacher.avatar_url
							},
							student: {
								studentId: exam.student.id,
								firstName: exam.student.first_name,
								lastName: exam.student.last_name,
								avatarUrl: exam.student.avatar_url
							},
							createAt: exam.date_created,
							startExamAt: exam.start_exam_at,
							endExamAt: exam.end_exam_at,
							isStarted: exam.is_exam_started,
							score: exam.score,
							answers: exam.questions.map((ans: any) => ({
								answerId: ans.id,
								studentAnswer: ans.student_answer,
								scoreValue: ans.score_value
							})),
							schoolTestQuestionsCount: exam.school_test.questions.length
						}
						allSchoolExams.push(schoolExamObject)
					})
					state.examsList = allSchoolExams
				})
				.addCase(getMySchoolExams.rejected, (state: SchoolTestSliceType, action) => {
					state.loadingGetExams = false
					state.errorGetExams = action.error.message
				}),
			builder
				.addCase(createSchoolTestQuestion.pending, (state: SchoolTestSliceType) => {
					state.errorCreateSchoolTestQuestion = undefined
					state.loadingCreateSchoolTestQuestion = true
				})
				.addCase(createSchoolTestQuestion.fulfilled, (state: SchoolTestSliceType) => {
					state.loadingCreateSchoolTestQuestion = false
				})
				.addCase(createSchoolTestQuestion.rejected, (state: SchoolTestSliceType, action) => {
					state.loadingCreateSchoolTestQuestion = false
					state.errorCreateSchoolTestQuestion = action.error.message
				}),
			builder
				.addCase(createSchoolTest.pending, (state: SchoolTestSliceType) => {
					state.errorCreateSchoolTest = undefined
					state.loadingCreateSchoolTest = true
				})
				.addCase(createSchoolTest.fulfilled, (state: SchoolTestSliceType) => {
					state.loadingCreateSchoolTest = false
				})
				.addCase(createSchoolTest.rejected, (state: SchoolTestSliceType, action) => {
					state.loadingCreateSchoolTest = false
					state.errorCreateSchoolTest = action.error.message
				}),
			builder
				.addCase(createSchoolExam.pending, (state: SchoolTestSliceType) => {
					state.errorCreateExam = undefined
					state.loadingCreateExam = true
				})
				.addCase(createSchoolExam.fulfilled, (state: SchoolTestSliceType) => {
					state.loadingCreateExam = false
				})
				.addCase(createSchoolExam.rejected, (state: SchoolTestSliceType, action) => {
					state.loadingCreateExam = false
					state.errorCreateExam = action.error.message
				}),
			builder
				.addCase(editSchoolTest.pending, (state: SchoolTestSliceType) => {
					state.errorEditSchoolTest = undefined
					state.loadingEditSchoolTest = true
				})
				.addCase(editSchoolTest.fulfilled, (state: SchoolTestSliceType) => {
					state.loadingEditSchoolTest = false
				})
				.addCase(editSchoolTest.rejected, (state: SchoolTestSliceType, action) => {
					state.loadingEditSchoolTest = false
					state.errorEditSchoolTest = action.error.message
				})
		builder
			.addCase(editSchoolTestQuestion.pending, (state: SchoolTestSliceType) => {
				state.errorEditQuestion = undefined
				state.loadingEditQuestion = true
			})
			.addCase(editSchoolTestQuestion.fulfilled, (state: SchoolTestSliceType) => {
				state.loadingEditQuestion = false
			})
			.addCase(editSchoolTestQuestion.rejected, (state: SchoolTestSliceType, action) => {
				state.loadingEditQuestion = false
				state.errorEditQuestion = action.error.message
			})
		builder
			.addCase(deleteSchoolTest.pending, (state: SchoolTestSliceType) => {
				state.errorDeleteSchoolTest = undefined
				state.loadingDeleteSchoolTest = true
			})
			.addCase(deleteSchoolTest.fulfilled, (state: SchoolTestSliceType) => {
				state.loadingDeleteSchoolTest = false
			})
			.addCase(deleteSchoolTest.rejected, (state: SchoolTestSliceType, action) => {
				state.loadingDeleteSchoolTest = false
				state.errorDeleteSchoolTest = action.error.message
			})
		builder
			.addCase(deleteSchoolTestQuestion.pending, (state: SchoolTestSliceType) => {
				state.errorDeleteSchoolTestQuestion = undefined
				state.loadingDeleteSchoolTestQuestion = true
			})
			.addCase(deleteSchoolTestQuestion.fulfilled, (state: SchoolTestSliceType) => {
				state.loadingDeleteSchoolTestQuestion = false
			})
			.addCase(deleteSchoolTestQuestion.rejected, (state: SchoolTestSliceType, action) => {
				state.loadingDeleteSchoolTestQuestion = false
				state.errorDeleteSchoolTestQuestion = action.error.message
			})
		builder
			.addCase(getSchoolTestDetails.pending, (state: SchoolTestSliceType) => {
				state.errorSchoolTestDetails = undefined
				state.loadingSchoolTestDetails = true
			})
			.addCase(getSchoolTestDetails.fulfilled, (state: SchoolTestSliceType, action) => {
				state.loadingSchoolTestDetails = false
				state.schoolTestDetails.id = action.payload.id
				state.schoolTestDetails.name = action.payload.name
				state.schoolTestDetails.description = action.payload.description
				state.schoolTestDetails.excellentScoreValue = action.payload.excellent_score_value
				state.schoolTestDetails.veryGoodScoreValue = action.payload.very_good_score_value
				state.schoolTestDetails.goodScoreValue = action.payload.good_score_value
				state.schoolTestDetails.satisfyScoreValue = action.payload.satisfy_score_value
				state.schoolTestDetails.failScoreValue = action.payload.fail_score_value
				const questions = action.payload.questions.map((q: any) => ({
					text: q.question_text,
					correctAnswer: q.correct_answer,
					answers: [q.answer_a, q.answer_b, q.answer_c, q.answer_d].filter((ans: string) => ans !== ""),
					scoreValue: q.score_value,
					id: q.id
				}))
				state.schoolTestDetails.questions = questions

			})
			.addCase(getSchoolTestDetails.rejected, (state: SchoolTestSliceType, action) => {
				state.loadingSchoolTestDetails = false
				state.searchUsersList = []
				state.errorSchoolTestDetails = action.error.message
			}),
			builder
				.addCase(searchForUsers.pending, (state: SchoolTestSliceType) => {
					state.searchUsersError = undefined
					state.searchUsersLoading = true
				})
				.addCase(searchForUsers.fulfilled, (state: SchoolTestSliceType, actions) => {
					state.searchUsersLoading = false
					const allUsers: SearchUser[] = []
					actions.payload.forEach((user: any) => {
						const userObject = {
							id: user.id,
							firstName: user.first_name,
							lastName: user.last_name,
							email: user.email,
							avatar: user.avatar_url,
							isAutoCreated: user.is_auto_created,
							is_student: user.is_student,
							is_teacher: user.is_teacher,
							school: user.school,
						}
						allUsers.push(userObject)
					})
					state.searchUsersList = allUsers
					console.log(state.searchUsersList)
				})
				.addCase(searchForUsers.rejected, (state: SchoolTestSliceType, action) => {
					state.searchUsersLoading = false
					state.searchUsersError = action.error.message
				}),
			builder
				.addCase(getSchoolExamDetails.pending, (state: SchoolTestSliceType) => {
					state.errorExamDetails = undefined
					state.loadingExamDetails = true
				})
				.addCase(getSchoolExamDetails.fulfilled, (state: SchoolTestSliceType, action) => {
					state.loadingExamDetails = false
					state.examDetails.id = action.payload.id
					state.examDetails.schoolTest = {
						id: action.payload.school_test.id,
						name: action.payload.school_test.name,
						description: action.payload.school_test.description,
						excellentScoreValue: action.payload.school_test.excellent_score_value,
						veryGoodScoreValue: action.payload.school_test.very_good_score_value,
						goodScoreValue: action.payload.school_test.good_score_value,
						satisfyScoreValue: action.payload.school_test.satisfy_score_value,
						failScoreValue: action.payload.school_test.fail_score_value,
					},
					state.examDetails.teacher = {
						teacherId: action.payload.teacher.id,
						firstName: action.payload.teacher.first_name,
						lastName: action.payload.teacher.last_name,
						avatarUrl: action.payload.teacher.avatar_url
					}
					state.examDetails.student = {
						studentId: action.payload.student.id,
						firstName: action.payload.student.first_name,
						lastName: action.payload.student.last_name,
						avatarUrl: action.payload.student.avatar_url
					}
					state.examDetails.createAt = action.payload.date_created_exam
					state.examDetails.startExamAt = action.payload.start_exam_at
					state.examDetails.endExamAt = action.payload.end_exam_at
					state.examDetails.isStarted = action.payload.is_exam_started
					state.examDetails.score = action.payload.score
					state.examDetails.answers = action.payload.questions.map((ans: any) => ({
						answerId: ans.id,
						studentAnswer: ans.student_answer,
						scoreValue: ans.score_value,
						question: {
							questionId: ans.question.id,
							text: ans.question.question_text,
							answers: [ans.question.answer_a, ans.question.answer_b, ans.question.answer_c, ans.question.answer_d].filter((a: string) => a !== ""),
							correctAnswer: ans.question.correct_answer,
							scoreValue: ans.question.score_value,
						}
					}))
					state.examDetails.schoolTestQuestionsCount = action.payload.school_test.questions.length
				})
				.addCase(getSchoolExamDetails.rejected, (state: SchoolTestSliceType, action) => {
					state.loadingExamDetails = false
					state.errorExamDetails = action.error.message
				}),
			builder
				.addCase(startSchoolExam.pending, (state: SchoolTestSliceType) => {
					state.errorExamAnswer = undefined
					state.loadingExamAnswer = true
				})
				.addCase(startSchoolExam.fulfilled, (state: SchoolTestSliceType) => {
					state.loadingExamAnswer = false
				})
				.addCase(startSchoolExam.rejected, (state: SchoolTestSliceType, action) => {
					state.loadingExamAnswer = false
					state.errorExamAnswer = action.error.message
				}),
			builder
				.addCase(answerSchoolExamQuestions.pending, (state: SchoolTestSliceType) => {
					state.loadingExamStart = true
					state.errorExamStart = undefined
				})
				.addCase(answerSchoolExamQuestions.fulfilled, (state: SchoolTestSliceType, action) => {
					state.loadingExamStart = false
					state.schoolExamTest.exam.examId = action.payload.exam.id
					state.schoolExamTest.exam.isStarted = action.payload.exam.is_exam_started
					state.schoolExamTest.exam.startAt = action.payload.exam.start_exam_at
					state.schoolExamTest.exam.endAt	= action.payload.exam.end_exam_at
					const questions: StartExamQuestionsType[] = []
					if(action.payload.questions) {
						action.payload.questions.forEach((question: any) => {
							const questionObject: StartExamQuestionsType = {
								id: question.id,
								questionText: question.question_text,
								answers: question.answers
							}
							questions.push(questionObject)
						})
						state.schoolExamTest.questions = questions
					}
				})
				.addCase(answerSchoolExamQuestions.rejected, (state: SchoolTestSliceType, action) => {
					state.loadingExamStart = false
					state.errorExamStart = action.error.message
				})
			builder
				.addCase(getMyStudentsSchoolExams.pending, (state: SchoolTestSliceType) => {
					state.errorStudentsExamsList = undefined
					state.loadingStudentsExamsList = true
				})
				.addCase(getMyStudentsSchoolExams.fulfilled, (state: SchoolTestSliceType, action) => {
					state.loadingStudentsExamsList = false
					state.counts = action.payload.count
					const pages = Math.ceil(action.payload.count / 20)
					state.pages = action.payload.count / 20 < 0 ? 2 : pages
					const allStudentsExams: StudentExamsType[] = []
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					action.payload.results.forEach((exam: any) => {
						const studentExamObject = {
							id: exam.id,
							teacher: {
								firstName: exam.teacher.first_name,
								lastName: exam.teacher.last_name,
								avatarUrl: exam.teacher.avatar_url
							},
							student: {
								studentId: exam.student.id,
								firstName: exam.student.first_name,
								lastName: exam.student.last_name,
								avatarUrl: exam.student.avatar_url
							},
							createAt: exam.date_created,
							startExamAt: exam.start_exam_at,
							endExamAt: exam.end_exam_at,
							isStarted: exam.is_exam_started,
							score: exam.score,
						}
						allStudentsExams.push(studentExamObject)
					})
					state.studentsExamsList = allStudentsExams
				})
				.addCase(getMyStudentsSchoolExams.rejected, (state: SchoolTestSliceType, action) => {
					state.loadingStudentsExamsList = false
					state.errorStudentsExamsList = action.error.message
				})
	}
})

export const actions = schoolTestsSlice.actions;

export const allSchoolTestsSelector = (state: RootState) => state[schoolTestsSlice.name];